import { ReferenceTypeConstants } from '@/constants/ReferenceTypeConstants';
import { Status } from '@/constants/Status';
import { SuspendStatus } from '@/constants/SuspendStatus';
import type {
  IPortfolioTreeSubportfolio,
  IPortfolioTreeStrategy,
  IPortfolioTree,
  IPortfolioTreeStrategyStaticInfo,
  ISetPortfolioTreeParams,
} from '@/types/IPortfolioTree';
import { isPortfolioTreeSubportfolio, isPortfolioTreeStrategy, IPortfolioTreeChildType } from '@/types/IPortfolioTree';
import type { ISidebarRow, ISidebarRowSubportfolio } from '@/types/ISidebarRow';
import { isISidebarRowSubportfolio } from '@/types/ISidebarRow';
import type { Ref } from 'vue';
import { computed } from 'vue';
import { set } from 'vue-demi';
import { AssetClassConstants } from '@/constants/AssetClassConstants';
import { Currency } from '@/constants/Currency';
import { FactorConstants } from '@/constants/FactorConstants';
import { FxConversion } from '@/constants/FxConversion';
import { Region } from '@/constants/Region';
import { StyleConstants } from '@/constants/StyleConstants';
import { PortfolioType } from '@/types/portfolio';
import type { IStrategy } from '@/types/strategy';
import type { StrategyItemResponseDTO } from '@/api-v2/web/discover';
import type { AddStrategySubset } from '@/views/modals/AddStrategyToPortfolioModal/AddStrategySubset';
import { WeightingTypeConstants } from '@/types/portfolio/AllocationWeightingConstants';
import useStrategyMask from '@/composables/useStrategyMask';
import type { IFactsheetDatum } from '@/types/IFactsheetData';
import { v4 as uuidv4 } from 'uuid';
import type { AllocationDTO } from '@/types/portfolio/AllocationDTO';
import { ReturnInterval } from '@/constants/ReturnInterval';
import { RebalancingMonthlyMonthlyReturnInterval } from '@/constants/RebalancingMonthlyMonthlyReturnInterval';
import { LeverageTypeConstants } from '@/types/portfolio/AllocationLeverageConstants';
import { RebalancingTypeConstants, RebalancingQuarterlyMonths } from '@/types/portfolio/AllocationRebalancingConstants';
import useAnalysisModes from '@/composables/useAnalysisModes';
import type { IPortfolioTreeTracksQuery } from '@/types/IPortfolioTreeTracksQuery';
import usePeriod from '@/composables/usePeriod';
import useFxConversion from '@/composables/useFxConversion';
import type { Period } from '@/types/period';
import type { StrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import { isStrategyFactsheetResponseDTO } from '@/api-v2/web/strategies';
import { BasketStatusConstants } from '@/constants/BasketStatusConstants';
import { BasketEventStatusConstants } from '@/constants/BasketEventStatusConstants';
import { useItemUnderAnalysisGlobalState } from '@/composables/useItemUnderAnalysis';
import { ReturnTypeConstants } from '@/constants/ReturnTypeConstants';
import { DEFAULT_BASKET_DIVISOR, getDefaultNotionalForPortfolio } from './portfolioDefaults';
import { DateTime } from 'luxon';
import type { IItemUnderAnalysis } from '@/types/IItemUnderAnalysis';
import type { GenericUniverseRecord } from '@/composables/useEquityScreenerTab';
import type { PortfolioExplorerRow } from '@/types/PortfolioExplorerRow';

export const findParentSubportfolio = (
  portfolioTree: IPortfolioTreeSubportfolio,
  portfolioTreeId: string,
): IPortfolioTreeSubportfolio | undefined => {
  // root portfolio doesn't have a parent
  if (portfolioTree.portfolioTreeId === portfolioTreeId) return portfolioTree;

  for (const component of portfolioTree.components) {
    // if the component is the one we're looking for, return the parent
    if (component.portfolioTreeId === portfolioTreeId) return portfolioTree;

    if (isPortfolioTreeSubportfolio(component)) {
      const parentPortfolio = findParentSubportfolio(component, portfolioTreeId);
      if (parentPortfolio) return parentPortfolio;
    }
  }
};

/**
 * Find all the ancestors of an item in the portfolio tree
 */
export const findAllAncestors = (
  { portfolioTree }: IPortfolioTree,
  portfolioTreeId: string,
): Map<string, IPortfolioTreeSubportfolio> => {
  const pinnedItemParentsMap = new Map<string, IPortfolioTreeSubportfolio>();

  let idToFind = portfolioTreeId;
  // While we have not reached the master portfolio tree, keep looking for the next parent
  while (idToFind !== portfolioTree.portfolioTreeId) {
    const parentOfPinnedItem = findParentSubportfolio(portfolioTree, idToFind);
    if (!parentOfPinnedItem) break;

    pinnedItemParentsMap.set(parentOfPinnedItem.portfolioTreeId, parentOfPinnedItem);
    // New id to find its parent
    idToFind = parentOfPinnedItem.portfolioTreeId;
  }

  return pinnedItemParentsMap;
};

/**
 * Get tree ids of all children and grandchildren.
 *
 * Previously this tried to keep track of which ones were active and which ones were not,
 * however this led to some very messy code.
 *
 * Now this function returns all the descendant ids, regardless of their active status
 */
export const getAllDescendantIds = ({
  subportfolioRow,
  ids = [],
  includeSubportfolios = true,
}: {
  subportfolioRow: ISidebarRowSubportfolio;
  ids?: ISidebarRow[];
  includeSubportfolios?: boolean;
}): ISidebarRow[] => {
  for (const child of subportfolioRow.children) {
    if (!isISidebarRowSubportfolio(child)) {
      ids.push(child);
      continue;
    }

    if (includeSubportfolios) ids.push(child);

    getAllDescendantIds({ subportfolioRow: child, ids });
  }
  return ids;
};

export type PortfolioTreeItemById = Map<string, IPortfolioTreeSubportfolio | IPortfolioTreeStrategy>;

export type PortfolioTreeByStrategyCode = Map<string, IPortfolioTreeStrategy>;

export const findTreeComponent = (
  portfolioTree: IPortfolioTreeSubportfolio,
  portfolioTreeIdToFind: string,
): IPortfolioTreeSubportfolio | IPortfolioTreeStrategy | undefined => {
  if (portfolioTree.portfolioTreeId === portfolioTreeIdToFind) {
    return portfolioTree;
  }

  for (const component of portfolioTree.components) {
    // if the component is the one we're looking for, return the component itself
    if (component.portfolioTreeId === portfolioTreeIdToFind) return component;

    if (isPortfolioTreeSubportfolio(component)) {
      const subPortfolio = findTreeComponent(component, portfolioTreeIdToFind);
      if (subPortfolio) return subPortfolio;
    }
  }
};

/**
 * Previously, this function would go DEEP first, meaning that even if the strategy you were looking for was on the first
 * tree layer, if there was a subportfolio before that strategy, it would go into that subportfolio first and try to
 * find a match. If it did find a match, it would return the strategy two levels down, instead of the strategy
 * on the first layer.
 *
 * This leads to some unexpected behavior with the hoverability of strategies that are duplicated, so I am changing
 * it to exhaustively search a layer before going another level down.
 */
export const findTreeComponentByStrategyCode = (
  portfolioTree: IPortfolioTreeSubportfolio,
  strategyCode: string,
): IPortfolioTreeStrategy | undefined => {
  for (const component of portfolioTree.components.filter(isPortfolioTreeStrategy)) {
    if ((component.strategy?.code ?? component.reference) === strategyCode) return component;
  }

  for (const component of portfolioTree.components) {
    if (isPortfolioTreeSubportfolio(component)) {
      const strategy = findTreeComponentByStrategyCode(component, strategyCode);
      if (strategy) return strategy;
    }
  }
};

export const suspendAll = async (
  portfolioTree: IPortfolioTreeSubportfolio,
  portfolioUnderFocusId: string,
): Promise<void> => {
  for (const component of portfolioTree.components) {
    if (component.suspended === SuspendStatus.ACTIVE) set(component, 'suspended', SuspendStatus.SUSPENDED);

    if (isPortfolioTreeSubportfolio(component)) {
      suspendAll(component, portfolioUnderFocusId);
    }
  }
};

export const isUnrecognizedPortfolioTreeStrategy = (
  item: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy,
): boolean => {
  if (!isPortfolioTreeStrategy(item)) return false;

  return item.status === Status.INACTIVE && !!item.reference && item.referenceType === ReferenceTypeConstants.TICKER;
};

export const isItemInactive = ({
  item,
  isOnConstituentRisk,
  isOnPosition,
  isOnPortfolioConstructionComposition,
}: {
  item: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy | null | undefined;
  isOnConstituentRisk: boolean;
  isOnPosition: boolean;
  isOnPortfolioConstructionComposition: boolean;
}): boolean => {
  if (item == null) return false;

  // A subportfolio is inactive if ALL of its children are inactive
  if (isPortfolioTreeSubportfolio(item)) {
    if (item.components.length === 0) return true;

    return item.components.every((component) =>
      isItemInactive({
        item: component,
        isOnConstituentRisk,
        isOnPosition,
        isOnPortfolioConstructionComposition,
      }),
    );
  }

  const { dataAccessibility, dataAvailability } = item;

  // ACCESSIBILITY - Track

  // If the user is on position composition, they already have access to the position
  // And we only need to check for track access
  if (isOnPortfolioConstructionComposition && dataAccessibility?.track === true) {
    return false;
  }

  // If the user is not on position and constituent risk, we need to know if they have access to the track
  if (!isOnPosition && !isOnConstituentRisk && dataAccessibility?.track === false) {
    return true;
  }

  // ACCESSIBILITY - Position
  // If the user is on position or constituent risk, we need to know if they have access to the position
  if ((isOnPosition || isOnConstituentRisk) && dataAccessibility?.position === false) {
    return true;
  }

  // ACCESSIBILITY - Risk
  // If the user is on constituent risk, we need to know if they have access to the risk
  if (isOnConstituentRisk && dataAccessibility?.risk === false) {
    return true;
  }

  // AVAILABILITY - Track
  // If the user is not on position and constituent risk, we only need to know if the track is available
  if (!isOnPosition && !isOnConstituentRisk && dataAvailability?.track === false) {
    return true;
  }

  // AVAILABILITY - Position
  // If the user is on position or constituent risk, we need to know if the position is available
  if ((isOnPosition || isOnConstituentRisk) && dataAvailability?.position === false) {
    return true;
  }

  // AVAILABILITY - Risk
  // If the user is on constituent risk, we need to know if the risk is available
  if (isOnConstituentRisk && dataAvailability?.risk === false) {
    return true;
  }

  // AVAILABILITY - Static Data
  // According to John, no static data does not affect the inactive status

  if (isUnrecognizedPortfolioTreeStrategy(item)) {
    return true;
  }

  return false;
};

export const createSubportfolio = (
  name: string,
  portfolioTreeUnderAnalysis: IPortfolioTree | null,
  toCurrency?: Currency,
  fxType?: FxConversion,
): IPortfolioTreeSubportfolio | undefined => {
  if (!portfolioTreeUnderAnalysis) return;

  let rootPortfolioType = PortfolioType.PORTFOLIO;
  if (portfolioTreeUnderAnalysis.portfolioTree.type === PortfolioType.EQUITY_BASKET) {
    rootPortfolioType = PortfolioType.EQUITY_BASKET;
  }

  if (portfolioTreeUnderAnalysis.portfolioTree.type === PortfolioType.QIS_BASKET) {
    rootPortfolioType = PortfolioType.QIS_BASKET;
  }

  const uuid = uuidv4();
  const options: AllocationDTO = {
    weighting: {
      type:
        rootPortfolioType === PortfolioType.PORTFOLIO ? WeightingTypeConstants.EQUAL : WeightingTypeConstants.MANUAL,
      returnInterval: ReturnInterval.DAILY,
    },
    rebalancing: {
      type: RebalancingTypeConstants.QUARTERLY,
      month: RebalancingQuarterlyMonths.MAR,
      date: RebalancingMonthlyMonthlyReturnInterval['LAST_DAY'],
    },
    leverage: {
      type: LeverageTypeConstants.TARGET_EXPOSURE,
      targetExposure: 100,
    },
  };
  set(portfolioTreeUnderAnalysis.allocation, uuid, options);

  const tree = createEmptyPortfolioTreeSubportfolio({
    name,
    rootPortfolioType,
    isMaster: false,
    options: {
      portfolioTreeId: uuid,
      toCurrency,
      fxType,
    },
  });

  return tree;
};

/**
 * Creates the list of properties needed for a new subportfolio added client side
 *
 * The rootPortfolioType is this situation can only be PORTFOLIO, EQUITY_BASKET, or QIS_BASKET
 * For CONSTITUENT we have the same logic as PORTFOLIO
 *
 * If you include a pointerSlug in the options parameter, the referenced portfolio will be created as a linked portfolio
 */
export const createEmptyPortfolioTreeSubportfolio = ({
  name,
  rootPortfolioType = PortfolioType.PORTFOLIO,
  isMaster = true,
  options,
}: {
  name: string;
  rootPortfolioType?: PortfolioType;
  isMaster?: boolean;
  options?: Partial<IPortfolioTreeSubportfolio>;
}): IPortfolioTreeSubportfolio => {
  return {
    portfolioTreeId: crypto.randomUUID(),
    // A new subportfolio should always have type PORTFOLIO
    type: isMaster ? rootPortfolioType : PortfolioType.PORTFOLIO,
    suspended: SuspendStatus.ACTIVE,
    status: Status.ACTIVE,
    name,
    components: [],
    weighting: 0,
    // An Equity basket should have asset class Equity
    assetClass:
      rootPortfolioType === PortfolioType.EQUITY_BASKET ? AssetClassConstants.EQUITY : AssetClassConstants.MULTI_ASSETS,
    region: Region.GLOBAL,
    factor: FactorConstants.MULTI,
    // An equity basket should always be long only
    style: rootPortfolioType === PortfolioType.EQUITY_BASKET ? StyleConstants.SMART_BETA : StyleConstants.BETA,
    // An equity basket should always have fx type UNHEDGED
    fxType: rootPortfolioType === PortfolioType.EQUITY_BASKET ? FxConversion.UNHEDGED : FxConversion.NOT_CONVERTED,
    toCurrency: Currency.USD,
    isInherited: true,
    description: '',
    portfolioValue: getDefaultNotionalForPortfolio(rootPortfolioType),
    // Properties only to be set for a root level of a basket
    ...([PortfolioType.EQUITY_BASKET, PortfolioType.QIS_BASKET].includes(rootPortfolioType) &&
      isMaster && {
        basketStatus: BasketStatusConstants.DRAFT,
        basketBusinessEvent: BasketEventStatusConstants.CREATION,
        returnType:
          rootPortfolioType === PortfolioType.EQUITY_BASKET
            ? ReturnTypeConstants.PRICE_RETURN
            : ReturnTypeConstants.EXCESS_RETURN,
        basketDivisor: DEFAULT_BASKET_DIVISOR,
        initialPrice: 0,
      }),
    apiConfig: null,
    ...options,
  };
};

export const createClientSideEmptyPortfolio = (
  name: string,
  rootPortfolioType: PortfolioType,
): ISetPortfolioTreeParams => {
  const uuid = uuidv4();

  return {
    portfolioTree: createEmptyPortfolioTreeSubportfolio({
      name,
      rootPortfolioType,
      options: { portfolioTreeId: uuid },
    }),
    allocation: {
      // Only to be set if a basket
      // We want to set the default allocation for a basket to be manual
      // Baskets should have leverage set to NONE
      ...([PortfolioType.EQUITY_BASKET, PortfolioType.QIS_BASKET].includes(rootPortfolioType) && {
        [uuid]: {
          weighting: {
            type: WeightingTypeConstants.MANUAL,
            returnInterval: ReturnInterval.DAILY,
          },
          rebalancing: {
            type: RebalancingTypeConstants.QUARTERLY,
            month: RebalancingQuarterlyMonths.MAR,
            date: RebalancingMonthlyMonthlyReturnInterval['LAST_DAY'],
          },
          leverage: {
            type: LeverageTypeConstants.NONE,
          },
        },
      }),
    },
    isBenchmark: false,
  };
};

// Todo: add a new API to fetch the strategy static info specifically for portfolio trees.
// For time being, just make sure the types look correct.
function convertToIPortfolioTreeStrategyStaticInfo(
  o: IStrategy | AddStrategySubset | StrategyItemResponseDTO | GenericUniverseRecord,
): IPortfolioTreeStrategyStaticInfo {
  if ('price' in o) {
    return {
      id: o.timeSeriesEntityId,
      code: o.code,
      shortName: o.shortName,
    } as IPortfolioTreeStrategyStaticInfo;
  }

  if (!('type' in o)) {
    return {
      id: o.id,
      code: o.code,
      shortName: o.shortName,
    } as IPortfolioTreeStrategyStaticInfo;
  }

  return {
    id: o.id,
    code: o.code,
    trackType: o.trackType,
    type: o.type,
    shortName: o.shortName,
    longName: o.longName,
    assetClass: o.assetClass,
    factor: o.factor,
    style: o.style,
    returnType: null,
    currency: o.currency,
    internalReference: o.internalReference ?? null,
    isin: o.isin ?? [],
    morningStarId: ('morningStarId' in o && o.morningStarId) || [],
    ric: ('ric' in o && o.ric) || [],
    ticker: ('ticker' in o && o.ticker) || [],
    sedol: o.sedol ?? [],
    historyStartDate: o.historyStartDate ?? '',
    effectiveHistoryStartDate: o.historyStartDate ?? '',
    inceptionDate: o.inceptionDate ?? '',
    provider: o.provider ?? '',
    region: o.region,
    sector: 'sector' in o ? o.sector : undefined,
    country: 'country' in o ? o.country : undefined,
  };
}

/**
 * Creates the list of properties needed for a new strategy added client side
 * @param o TimeSeries data
 */
export const createBlankPortfolioTreeStrategy = (
  o: AddStrategySubset | StrategyItemResponseDTO | GenericUniverseRecord,
): IPortfolioTreeStrategy => {
  const { masterPortfolioTreeTempRef } = useItemUnderAnalysisGlobalState();

  if ('isProxy' in o) {
    return {
      portfolioTreeId: crypto.randomUUID(),
      type: IPortfolioTreeChildType.TIMESERIES,
      crossReferenceId: o.code,
      suspended: SuspendStatus.ACTIVE,
      status: Status.ACTIVE,
      strategy: convertToIPortfolioTreeStrategyStaticInfo(o),
      weighting: 0,
      toCurrency: masterPortfolioTreeTempRef.value
        ? masterPortfolioTreeTempRef.value.portfolioTree.toCurrency
        : Currency.USD,
      fxType: masterPortfolioTreeTempRef.value
        ? masterPortfolioTreeTempRef.value.portfolioTree.fxType
        : FxConversion.NOT_CONVERTED,
      isInherited: true,
    };
  }

  return {
    portfolioTreeId: crypto.randomUUID(),
    type: IPortfolioTreeChildType.TIMESERIES,
    timeseriesId: 'timeSeriesEntityId' in o ? o.timeSeriesEntityId : o.id,
    suspended: SuspendStatus.ACTIVE,
    status: Status.ACTIVE,
    strategy: convertToIPortfolioTreeStrategyStaticInfo(o),
    weighting: 0,
    toCurrency: masterPortfolioTreeTempRef.value
      ? masterPortfolioTreeTempRef.value.portfolioTree.toCurrency
      : Currency.USD,
    fxType: masterPortfolioTreeTempRef.value
      ? masterPortfolioTreeTempRef.value.portfolioTree.fxType
      : FxConversion.NOT_CONVERTED,
    isInherited: true,
  };
};

/**
 * Function to distinguish between a saved portfolio and a portfolio that is in the process of being created
 */
export const hasPortfolioTreeBeenSaved = (tree: ISetPortfolioTreeParams | IPortfolioTree): tree is IPortfolioTree => {
  return 'originalSlug' in tree;
};

/**
 * Determines if the portfolio is a live object
 * This means we receive daily snapshots for this object
 * Usually can be a constituent portfolio or a basket
 *
 * This function assumes that only a saved portfolio can be Live
 */
export const isLivePortfolioFn = (tree: IPortfolioTree | ISetPortfolioTreeParams | null): boolean | null => {
  if (tree === null) return null;

  return hasPortfolioTreeBeenSaved(tree) && tree.portfolioListId != null;
};

export const isConstituentPortfolioFn = (tree: IPortfolioTree | ISetPortfolioTreeParams | null): boolean | null => {
  if (tree === null) return null;

  return tree.portfolioTree.type === PortfolioType.CONSTITUENT;
};

export const hasCustomRebalancingPortfolioFn = (
  tree: IPortfolioTree | ISetPortfolioTreeParams | null,
): boolean | null => {
  if (tree === null) return null;

  return ('parameters' in tree && tree.parameters?.hasCustomRebalancing) ?? false;
};

/**
 * A user may create an equity basket, but it is not determined to be 'live' until the approval process is complete
 * and the data side tags it as a live equity basket, see `isTaggedAsLiveEquityBasketFn`, below.
 */
export const isEquityBasketPortfolioFn = (tree: IPortfolioTree | ISetPortfolioTreeParams | null): boolean | null => {
  if (tree === null) return null;

  return tree.portfolioTree.type === PortfolioType.EQUITY_BASKET;
};

export const isQISBasketPortfolioFn = (tree: IPortfolioTree | ISetPortfolioTreeParams | null): boolean | null => {
  if (tree === null) return null;

  return tree.portfolioTree.type === PortfolioType.QIS_BASKET;
};

/**
 * Mirrors API `isUnleveraged` (explicit `apiConfig.hasLeverage` when present; otherwise equity baskets only are unleveraged).
 * A null tree counts as not leveraged. `ISetPortfolioTreeParams` has no apiConfig.
 */
export const isLeveragedBasketFn = (tree: IPortfolioTree | ISetPortfolioTreeParams | null): boolean => {
  if (tree === null) return false;

  const hasLeverage = 'apiConfig' in tree ? tree.apiConfig?.hasLeverage : undefined;

  if (hasLeverage == null) return isEquityBasketPortfolioFn(tree) !== true;

  return hasLeverage;
};

export const isBasketTypePortfolioFn = (
  portfolioTree: IPortfolioTree | ISetPortfolioTreeParams | null,
): boolean | null => {
  if (portfolioTree === null) return null;

  return isEquityBasketPortfolioFn(portfolioTree) || isQISBasketPortfolioFn(portfolioTree);
};

export const isPeerGroupTypePortfolioFn = (tree: IPortfolioTree | ISetPortfolioTreeParams | null): boolean | null => {
  if (tree === null) return null;

  return tree.portfolioTree.type === PortfolioType.PEER_GROUP;
};

/**
 * determines if the selected portfolio has a custom weighting type or not
 * used in PortfolioWeightToolbar and StaticDataModal
 *
 * @param portfolio portfolio tree with allocation data
 */
export const isPortfolioTreeCustomFn = (portfolio: IPortfolioTree | ISetPortfolioTreeParams | null): boolean | null => {
  if (portfolio === null) return null;

  return portfolio.allocation[portfolio.portfolioTree.portfolioTreeId].weighting.type === WeightingTypeConstants.CUSTOM;
};

/**
 * Determines whether the root subportfolio uses manual weighting.
 *
 * @param portfolio portfolio tree with allocation data
 */
export const isManualWeightedPortfolioFn = (
  portfolio: IPortfolioTree | ISetPortfolioTreeParams | null,
): boolean | null => {
  if (portfolio === null) return null;

  return portfolio.allocation[portfolio.portfolioTree.portfolioTreeId].weighting.type === WeightingTypeConstants.MANUAL;
};

/**
 * determines if portfolio/strategy has a pointerSlug or if it's parent has a pointerSlug
 * used in SidebarRow and StaticDataModal
 *
 * @param item any portfolio tree item or strategy with or without pointerSlug
 * @param isParentPointerPortfolio boolean to determine if parent is a pointer portfolio
 */
export const isPortfolioTreePointerFn = (
  item: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy | null | undefined,
  isParentPointerPortfolio?: boolean,
): boolean | null => {
  if (item == null) return null;

  if (isPortfolioTreeSubportfolio(item)) {
    if (item.pointerSlug) {
      return true;
    }
  }

  if (isParentPointerPortfolio) return isParentPointerPortfolio;

  return false;
};

export const findDatumAndReturnDisplayName = ({
  datumColorMapId,
  dataset,
}: {
  datumColorMapId: string;
  dataset: (IPortfolioTreeSubportfolio | IPortfolioTreeStrategy | IFactsheetDatum)[];
}): string | undefined => {
  const { getStrategyMask } = useStrategyMask();

  const datum = dataset.find((datum) => {
    if ('code' in datum) return datum.code === datumColorMapId;

    if ('portfolioId' in datum) return datum.portfolioId === datumColorMapId;

    if ('portfolioTreeId' in datum) return datum.portfolioTreeId === datumColorMapId;

    if ('strategy' in datum) return datum.strategy.code === datumColorMapId;

    datum satisfies never;
  });
  if (!datum) return;

  return getStrategyMask(datum);
};

/**
 * Flatten the portfolio tree to a list of components
 */
export const flattenPortfolioTree = (
  tree: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy,
): (IPortfolioTreeSubportfolio | IPortfolioTreeStrategy)[] => {
  const rows: (IPortfolioTreeSubportfolio | IPortfolioTreeStrategy)[] = [];
  appendPortfolioTreeNodes(tree, rows);
  return rows;
};

const appendPortfolioTreeNodes = (
  node: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy,
  rows: (IPortfolioTreeSubportfolio | IPortfolioTreeStrategy)[],
): void => {
  rows.push(node);

  if ('components' in node) {
    for (const child of node.components) {
      appendPortfolioTreeNodes(child, rows);
    }
  }
};

const appendPortfolioTreeItemToMap = (
  node: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy,
  itemsById: PortfolioTreeItemById,
): void => {
  itemsById.set(node.portfolioTreeId, node);

  if ('components' in node) {
    for (const child of node.components) {
      appendPortfolioTreeItemToMap(child, itemsById);
    }
  }
};

/**
 * Index a portfolio tree's items by portfolioTreeId. Mutates `itemsById` so callers can
 * reuse one map for many O(1) lookups instead of walking the tree each time.
 */
export const flattenPortfolioTreeToMap = ({
  node,
  itemsById,
}: {
  node: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy;
  itemsById: PortfolioTreeItemById;
}): void => {
  appendPortfolioTreeItemToMap(node, itemsById);
};

export const buildPortfolioTreeItemsById = (
  root?: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy,
): PortfolioTreeItemById => {
  const itemsById: PortfolioTreeItemById = new Map();
  if (root) {
    appendPortfolioTreeItemToMap(root, itemsById);
  }

  return itemsById;
};

/**
 * Index strategies by code with the same first-match order as
 * `findTreeComponentByStrategyCode`: current-layer strategies, then each
 * subportfolio in `components` order. First write wins for duplicate codes.
 */
export const flattenPortfolioTreeToStrategyCodeMap = ({
  node,
  portfolioTreeByStrategyCode,
}: {
  node: IPortfolioTreeSubportfolio;
  portfolioTreeByStrategyCode: PortfolioTreeByStrategyCode;
}): void => {
  for (const component of node.components) {
    if (!isPortfolioTreeStrategy(component)) continue;

    const strategyCode = component.strategy?.code ?? component.reference;
    if (!strategyCode || portfolioTreeByStrategyCode.has(strategyCode)) continue;

    portfolioTreeByStrategyCode.set(strategyCode, component);
  }

  for (const component of node.components) {
    if (!isPortfolioTreeSubportfolio(component)) continue;

    flattenPortfolioTreeToStrategyCodeMap({ node: component, portfolioTreeByStrategyCode });
  }
};

export const getTrackQuery = (period?: Ref<Period>): Ref<IPortfolioTreeTracksQuery> => {
  const { period: defaultPeriod, returnInterval } = usePeriod();
  const { fxType, toCurrency } = useFxConversion();
  const { itemUnderAnalysis } = useItemUnderAnalysisGlobalState();
  const { isRegimeAnalysisActive, storeRegimeId } = useAnalysisModes();

  return computed((): IPortfolioTreeTracksQuery => {
    return {
      startDate: period ? period.value.fromDateString() : defaultPeriod.value.fromDateString(),
      endDate: period ? period.value.toDateString() : defaultPeriod.value.toDateString(),
      returnInterval: returnInterval.value,
      codes:
        itemUnderAnalysis.value && isPortfolioTreeStrategy(itemUnderAnalysis.value) && itemUnderAnalysis.value.strategy
          ? [itemUnderAnalysis.value.strategy.code]
          : [],
      treeIds:
        itemUnderAnalysis.value && isPortfolioTreeSubportfolio(itemUnderAnalysis.value)
          ? [itemUnderAnalysis.value.portfolioTreeId]
          : [],
      blmId: undefined,
      portfolioTreeId: undefined,
      regimeId: isRegimeAnalysisActive.value && storeRegimeId.value !== null ? storeRegimeId.value : undefined,

      /** TODO: Remove this when we invalidate queries based on draft portfolio API Call.
       * This does not need to be sent to the API as the fx conversion is handled by the draft portfolio
       * But I am adding it to the query so that it can refetch this api based on the changes
       */
      fxType: fxType.value,
      toCurrency: toCurrency.value,
    };
  });
};

/**
 * Prevents an essentially duplicate call from going out by ignoring the portfolioTreeId when
 * the master portfolio tree is the item under analysis.
 *
 * NOTE: not all queries support returning undefined, so pass `allowUndefined: false` if you need this id no matter what
 */
export const useGetItemUnderAnalysisPortfolioTreeId = (
  {
    allowUndefined,
  }: {
    allowUndefined: boolean;
  } = {
    allowUndefined: true,
  },
): Ref<string | undefined> => {
  const { itemUnderAnalysis, draft, portfolioTreeUnderAnalysis } = useItemUnderAnalysisGlobalState();

  return computed((): string | undefined => {
    if (!itemUnderAnalysis.value || isStrategyFactsheetResponseDTO(itemUnderAnalysis.value)) {
      return undefined;
    }

    if (
      draft.value &&
      portfolioTreeUnderAnalysis.value &&
      draft.value.portfolioId !== portfolioTreeUnderAnalysis.value.portfolioId
    ) {
      return undefined;
    }

    if (draft.value && itemUnderAnalysis.value.portfolioTreeId === draft.value.portfolioId && allowUndefined) {
      return undefined;
    }

    return itemUnderAnalysis.value.portfolioTreeId;
  });
};

/**
 * Get the tree ids of all subportfolios from a given portfolioTree (recursive)
 */
export const getAllDescendantSubportfolioTreeIds = (portfolioTree: IPortfolioTreeSubportfolio): string[] => {
  return flattenPortfolioTree(portfolioTree)
    .filter(isPortfolioTreeSubportfolio)
    .map((x) => x.portfolioTreeId);
};

/**
 * Get the tree ids of all strategies reachable from given portfolioTree (recursive)
 */
export const getAllDescendantStrategy = (portfolioTree: IPortfolioTreeSubportfolio): IPortfolioTreeStrategy[] => {
  return flattenPortfolioTree(portfolioTree).filter(isPortfolioTreeStrategy);
};

/**
 * Check if the current portfolio is young (created in past 60 seconds).
 * This depends on the current time. So we put this in a function instead of computed.
 */
export const isPortfolioYoung = (tree: IPortfolioTree): boolean => {
  if (!tree) return false;

  const age = DateTime.fromISO(tree.createdAt).diffNow('seconds').seconds;

  return Math.abs(age) < 60;
};

/**
 * The root itemUnderAnalysis can be either a StrategyFactsheetResponseDTO or the portfolioTreeUnderAnalysis.
 * Depending on which page we are on, we switch the root itemUnderAnalysis used
 * This function returns the correct datum, based on the itemUnderAnalysis type
 */
export const getRootItemUnderAnalysis = (
  datum: IItemUnderAnalysis | null,
  portfolioTreeUnderAnalysis?: IPortfolioTree | null,
): IPortfolioTreeSubportfolio | StrategyFactsheetResponseDTO | null => {
  if (!datum) return null;

  if (isPortfolioTreeStrategy(datum)) {
    if (!portfolioTreeUnderAnalysis) return null;

    return portfolioTreeUnderAnalysis.portfolioTree;
  }

  return datum;
};

/**
 * "Official" is used intentionally instead of "live".
 * A master index can be official even when it represents a simulation version,
 * as long as it is tied to a `portfolioListId`.
 */
export const isOfficialMasterIndexFn = (datum: IPortfolioTreeSubportfolio | StrategyFactsheetResponseDTO): boolean => {
  if ('strategyApiConfig' in datum) {
    // Factsheet roots have no `reference`; presence of strategyApiConfig already means a live published strategy.
    return (
      datum.strategyApiConfig.shouldExposePositions === true && datum.strategyApiConfig.containsOnlyEquities === false
    );
  }

  const containsOnlyEquities = datum.apiConfig?.containsOnlyEquities ?? false;

  // `reference` distinguishes master indexes from sleeves that also expose positions.
  return datum.apiConfig?.shouldExposePositions === true && datum.reference != null && containsOnlyEquities === false;
};

/**
 * For live master indices, returns the reference code
 * Otherwise, returns the original slug
 */
export const getReferenceCodeOrSlug = (datum: IPortfolioTree): string => {
  if (
    (isOfficialMasterIndexFn(datum.portfolioTree) || isTaggedAsLiveEquityBasketFn(datum.portfolioTree)) &&
    datum.portfolioTree.reference
  ) {
    return datum.portfolioTree.reference;
  }

  return datum.originalSlug;
};

/**
 * Checks whether a given datum is tagged with `shouldExposePositions: true` and `containsOnlyEquities: true`, meaning
 * that this is a LIVE equity basket. A user may create an equity basket, but it is not determined to be live until
 * the approval process is complete and these tags have been added.
 *
 * Theoretically there should be no items that return `true` for this function and return `false` for
 * `isEquityBasketPortfolioFn`.
 *
 * Different possibilities and their effects:
 * - `isEquityBasketPortfolioFn` returns `true` and `isTaggedAsLiveEquityBasketFn` returns `true`: this is the happy case,
 *      where the basket has completed the approval process. Portfolio Construction will be available, but in all other
 *      respects, the item will be treated as a Strategy. It will be routed to the Strategy versions of Analytics pages,
 *      and the Application Sidebar Factsheet button will route to the Strategy Factsheet.
 * - `isEquityBasketPortfolioFn` returns `true` and `isTaggedAsLiveEquityBasketFn` returns `false`: the equity basket
 *      was created by the user, but the approval process has not yet been completed. During this stage, the item will
 *      be treated as a Portfolio in all respects. It will be routed to the Portfolio versions of Analytics pages,
 *      and the Application Sidebar Factsheet button will route to the Portfolio Factsheet.
 * - `isEquityBasketPortfolioFn` returns `false` and `isTaggedAsLiveEquityBasketFn` returns `true`: error case. However,
 *      the functionality will be the same as the happy case. Portfolio Construction will be available and in all other
 *      respects the item will be treated as a Strategy. It will be routed to the Strategy versions of Analytics pages
 *      and the Application Sidebar Factsheet button will route to the Strategy Factsheet.
 * - `isEquityBasketPortfolioFn` returns `false` and `isTaggedAsLiveEquityBasketFn` returns `false`: standard portfolio
 *      or strategy case. No special notes for this case.
 */
export const isTaggedAsLiveEquityBasketFn = (
  datum: IPortfolioTreeSubportfolio | StrategyFactsheetResponseDTO,
): boolean => {
  if ('strategyApiConfig' in datum) {
    // Factsheet roots have no `reference`; presence of strategyApiConfig already means a live published strategy.
    return (
      datum.strategyApiConfig.shouldExposePositions === true && datum.strategyApiConfig.containsOnlyEquities === true
    );
  }

  const containsOnlyEquities = datum.apiConfig?.containsOnlyEquities ?? false;

  // `reference` distinguishes live baskets from sleeves that also expose positions.
  return datum.apiConfig?.shouldExposePositions === true && datum.reference != null && containsOnlyEquities === true;
};

/**
 * Checks whether a given datum is a QIS of Stocks based on portfolio type and containsOnlyEquities.
 */
export const isQisOfOnlyStocksFn = (datum: IPortfolioTreeSubportfolio | null): boolean => {
  if (!isPortfolioTreeSubportfolio(datum)) return false;

  if (datum.type !== PortfolioType.QIS_BASKET) return false;

  return datum.apiConfig?.containsOnlyEquities === true;
};

export const convertPortfolioTreeComponentToPortfolioExplorerRow = ({
  component,
  ancestorPath,
  isParentSuspended,
}: {
  component: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy;
  ancestorPath: string[];
  isParentSuspended: boolean;
}): PortfolioExplorerRow => {
  return {
    component,
    path: [...ancestorPath, component.portfolioTreeId],
    isParentSuspended,
  };
};

const appendPortfolioExplorerRows = ({
  node,
  ancestorPath,
  isParentSuspended,
  rows,
}: {
  node: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy;
  ancestorPath: string[];
  isParentSuspended: boolean;
  rows: PortfolioExplorerRow[];
}): void => {
  const row = convertPortfolioTreeComponentToPortfolioExplorerRow({ component: node, ancestorPath, isParentSuspended });
  rows.push(row);

  if (isPortfolioTreeStrategy(node)) {
    return;
  }

  const isSuspendedHere = isParentSuspended || node.suspended === SuspendStatus.SUSPENDED;

  for (const child of node.components) {
    appendPortfolioExplorerRows({ node: child, ancestorPath: row.path, isParentSuspended: isSuspendedHere, rows });
  }
};

export const collectPortfolioExplorerRows = ({
  node,
  isParentSuspended,
  ancestorPath,
}: {
  node: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy;
  isParentSuspended: boolean;
  ancestorPath: string[];
}): PortfolioExplorerRow[] => {
  const rows: PortfolioExplorerRow[] = [];
  appendPortfolioExplorerRows({ node, ancestorPath, isParentSuspended, rows });
  return rows;
};

export const convertDraftToPortfolioExplorerRows = (draft: IPortfolioTree): PortfolioExplorerRow[] =>
  collectPortfolioExplorerRows({ node: draft.portfolioTree, ancestorPath: [], isParentSuspended: false });
